import type { ExpenseListQuery, Page } from '@chukua/common';
import { Table, type TableColumnsType } from 'antd';
import { CheckCheck, SearchX } from 'lucide-react';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ListSearch } from '../../../shared/components/ListSearch';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import type { ExpenseView } from '../expenses.api';
import { ApprovalFilters } from './ApprovalFilters';

interface ExpenseApprovalsTableProps {
  page: Page<ExpenseView>;
  columns: TableColumnsType<ExpenseView>;
  list: ListQueryControls<ExpenseListQuery>;
  isRefreshing: boolean;
}

/** Search, the driver, truck and age filters, and the server-paged queue; the old rows stay up, dimmed, while the next page loads. */
export function ExpenseApprovalsTable({ page, columns, list, isRefreshing }: ExpenseApprovalsTableProps) {
  // `useListQuery` counts search and multi-value filters; the age is a plain number it does not see.
  const isFiltered = list.isFiltered || list.query.minAgeHours !== undefined;
  return (
    <div className="flex flex-col gap-4">
      <ListSearch
        value={list.query.q}
        placeholder="Search trip (T-12), driver, truck, station or note"
        onSearch={(q) => list.setQuery({ q }, { replace: true })}
      />
      <ApprovalFilters list={list} />
      <Table
        rowKey="id"
        dataSource={page.items}
        columns={columns}
        loading={isRefreshing}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: isFiltered ? <NoMatchingExpenses /> : <NothingWaiting /> }}
        {...list.tableProps<ExpenseView>(page.total)}
      />
    </div>
  );
}

function NoMatchingExpenses() {
  return <EmptyState icon={SearchX} title="No matching expenses" description="No expenses waiting for you match this search or these filters." />;
}

function NothingWaiting() {
  return <EmptyState icon={CheckCheck} title="Nothing waiting for you" description="Expenses you can approve, up to what your role may, appear here." />;
}
